/**
 * «¿Este plano sirve para presupuestar?» (OPUS ci, idea 10): una pastilla con el veredicto en la barra
 * del plano; al tocarla, las razones. La primera vez que se abre un plano el detalle se despliega solo
 * (es el chequeo «al subir»); después queda en la pastilla. El cálculo vive en
 * @supernova/shared (chequeoDelPlano.ts) y sale del archivo: $0, sin IA.
 */
import { useEffect, useState } from 'react';
import { Popover, PopoverSurface, PopoverTrigger, makeStyles } from '@fluentui/react-components';
import { Checkmark16Filled, DismissCircle16Filled, Info16Regular, Warning16Filled } from '@fluentui/react-icons';
import { TITULO_VEREDICTO_CHEQUEO, type ChequeoDelPlano, type VeredictoChequeo } from '@supernova/shared';
import { DiagnosticoDelDibujoLista, type DiagnosticoDelDibujoProps } from './planos/DiagnosticoDelDibujo.js';

const CLAVE_VISTOS = 'supernova:chequeo-plano-vistos';

const useStyles = makeStyles({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '24px',
    padding: '0 10px',
    borderRadius: '12px',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-surface)',
    color: 'var(--text-secondary)',
    fontSize: '12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    ':hover': { background: 'var(--bg-hover)', color: 'var(--text-primary)' },
  },
  punto: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  surface: { width: '400px', maxWidth: 'calc(100vw - 24px)', padding: '14px 16px 12px', display: 'flex', flexDirection: 'column', gap: '10px' },
  veredicto: { display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '6px', padding: '8px 10px', fontWeight: 700, fontSize: '14px' },
  resumen: { margin: 0, fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.45 },
  ind: { display: 'flex', flexDirection: 'column', gap: '3px' },
  indFila: { display: 'flex', fontSize: '12px', color: 'var(--text-secondary)' },
  indValor: { fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' },
  barra: { height: '5px', background: 'var(--bg-raised)', borderRadius: '3px' },
  lista: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' },
  item: { display: 'flex', gap: '8px', fontSize: '13px', lineHeight: 1.4, color: 'var(--text-primary)', alignItems: 'flex-start' },
  icono: { flexShrink: 0, marginTop: '2px' },
  nota: { margin: 0, fontSize: '12px', color: 'var(--text-tertiary)', lineHeight: 1.4 },
});

const COLOR: Record<VeredictoChequeo, { color: string; fondo: string }> = {
  bien: { color: 'var(--ok)', fondo: 'var(--ok-weak)' },
  cuidado: { color: 'var(--warn)', fondo: 'var(--warn-weak)' },
  no: { color: 'var(--bad)', fondo: 'var(--bad-weak)' },
};

const ETIQUETA_CORTA: Record<VeredictoChequeo, string> = {
  bien: 'Sirve para presupuestar',
  cuidado: 'Sirve, con cuidado',
  no: 'Casi no sirve',
};

function leerVistos(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE_VISTOS) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function marcarVisto(id: string) {
  try {
    const v = leerVistos().filter((x) => x !== id);
    v.push(id);
    localStorage.setItem(CLAVE_VISTOS, JSON.stringify(v.slice(-200)));
  } catch {
    /* sin almacenamiento: se vuelve a desplegar la próxima vez, nada más */
  }
}

export interface ChequeoDelPlanoProps {
  chequeo: ChequeoDelPlano;
  /** Archivo del plano: la primera vez que se ve su chequeo, el detalle se despliega solo. */
  fileId: string;
  /** «Revisar el dibujo» (solo DWG, detrás de su bandera): sin esto la pastilla es la de siempre. */
  diagnostico?: DiagnosticoDelDibujoProps | null;
}

export function ChequeoDelPlanoPastilla({ chequeo, fileId, diagnostico }: ChequeoDelPlanoProps) {
  const s = useStyles();
  const [abierto, setAbierto] = useState(false);
  useEffect(() => {
    if (leerVistos().includes(fileId)) return;
    marcarVisto(fileId);
    setAbierto(true);
  }, [fileId]);
  const c = COLOR[chequeo.veredicto];
  return (
    <Popover withArrow open={abierto} onOpenChange={(_e, d) => setAbierto(d.open)} positioning={{ position: 'below', align: 'center' }}>
      <PopoverTrigger disableButtonEnhancement>
        <button type="button" className={s.trigger} style={{ borderColor: c.color }} aria-label="¿Este plano sirve para presupuestar?" title="¿Este plano sirve para presupuestar?">
          <span className={s.punto} style={{ background: c.color }} />
          {ETIQUETA_CORTA[chequeo.veredicto]}
        </button>
      </PopoverTrigger>
      <PopoverSurface className={s.surface} aria-label="¿Este plano sirve para presupuestar?">
        <div className={s.veredicto} style={{ background: c.fondo, color: c.color }}>
          {chequeo.veredicto === 'bien' ? <Checkmark16Filled /> : chequeo.veredicto === 'cuidado' ? <Warning16Filled /> : <DismissCircle16Filled />}
          {TITULO_VEREDICTO_CHEQUEO[chequeo.veredicto]}
        </div>
        {chequeo.resumen && <p className={s.resumen}>{chequeo.resumen}</p>}
        {chequeo.indicadores.map((i) => {
          const color = i.bueno ? 'var(--ok)' : i.pct > 0 ? 'var(--warn)' : 'var(--ok)';
          return (
            <div key={i.etiqueta} className={s.ind}>
              <div className={s.indFila}>
                <span style={{ flex: 1 }}>{i.etiqueta}</span>
                <span className={s.indValor}>
                  {Math.round(i.pct)} %{i.cuenta != null ? ` · ${i.cuenta.toLocaleString('es-MX')}` : ''}
                </span>
              </div>
              <div className={s.barra}>
                <div style={{ width: `${Math.min(100, i.pct)}%`, height: '100%', background: color, borderRadius: 3 }} />
              </div>
            </div>
          );
        })}
        <ul className={s.lista}>
          {chequeo.puntos.map((p) => (
            <li key={p.texto} className={s.item}>
              <span className={s.icono} style={{ color: p.tono === 'ok' ? 'var(--ok)' : p.tono === 'mal' ? 'var(--warn)' : 'var(--text-tertiary)' }}>
                {p.tono === 'ok' ? <Checkmark16Filled /> : p.tono === 'mal' ? <Warning16Filled /> : <Info16Regular />}
              </span>
              <span>{p.texto}</span>
            </li>
          ))}
        </ul>
        {diagnostico && (
          <DiagnosticoDelDibujoLista
            {...diagnostico}
            onVer={
              diagnostico.onVer
                ? (a) => {
                    diagnostico.onVer!(a);
                    setAbierto(false); // se cierra el globo para que se vea el visor
                  }
                : undefined
            }
          />
        )}
        <p className={s.nota}>{chequeo.nota} Revisarlo no gasta nada: son reglas del formato.</p>
      </PopoverSurface>
    </Popover>
  );
}
